// The design system's AlarmBanner (handoff `_ds` components/feedback/
// AlarmBanner.jsx; `.tb-alarm` in ui.css): a red capsule for an alarm that
// needs attention, its icon in an expanding ring while it rings, its name and
// what it measured, then Silence (while ringing) and Review. Presentational;
// the caller decides the alarm and does the silencing (TOM-512).

import { Link } from 'react-router-dom';

import { Icon } from './Icon';

export interface AlarmBannerProps {
  title: string;
  subtitle?: string | null;
  /** The alarm is sounding (Active): the ring pulses and Silence shows. */
  ringing: boolean;
  /** Other alarms that also need attention, said as "+N more". */
  more?: number;
  silence?: {
    label: string;
    /** Why it is disabled, as its tooltip. */
    title?: string;
    disabled: boolean;
    onClick: () => void;
  };
  reviewTo: string;
  /** A failed command's honest line, in place of the subtitle. */
  error?: string | null;
  testId?: string;
}

export function AlarmBanner({
  title,
  subtitle,
  ringing,
  more = 0,
  silence,
  reviewTo,
  error,
  testId,
}: AlarmBannerProps) {
  const line = error ?? subtitle ?? null;
  return (
    <div
      className={`tb-alarm${ringing ? ' is-ringing' : ''}`}
      role="alert"
      data-testid={testId}
      data-ringing={ringing}
    >
      <span className="tb-alarm__ring" aria-hidden="true">
        <Icon name="alarm" size={20} strokeWidth={2.2} />
      </span>
      <span className="tb-alarm__text">
        <span className="tb-alarm__title">{title}</span>
        {line !== null || more > 0 ? (
          <span className={`tb-alarm__subtitle${error ? ' is-error' : ''}`}>
            {line !== null ? <span className="tb-alarm__line">{line}</span> : null}
            {/* The count keeps its room; the measured line gives way on a phone. */}
            {more > 0 ? <span className="tb-alarm__more">+{more} more</span> : null}
          </span>
        ) : null}
      </span>
      {ringing && silence !== undefined ? (
        <button
          type="button"
          className="tb-alarm__pill tb-alarm__pill--silence"
          disabled={silence.disabled}
          title={silence.title}
          aria-label={silence.label}
          onClick={silence.onClick}
        >
          Silence
        </button>
      ) : null}
      <Link className="tb-alarm__pill tb-alarm__pill--review" to={reviewTo}>
        Review
      </Link>
    </div>
  );
}
